// TODO: watch for update to stream-readable OR simple-peer
// SimplePeer's stream-readable dep has an issue with webpack 5
// below is a solution which doesn't work in our case (conflicts with other libs)
// https://github.com/feross/simple-peer/issues/767
// so we manually add the shim here before loading simple-peer
// package.json.devDependencies:  "process": "^0.11.10"
import { Chunk, isChunk } from 'chunkosaurus';
import Events from 'events';
import processBrowserShim from 'process/browser';
import { SbMediaStreamType, SbMediaStreams } from 'sb-lib/media-streams';
import { RrwebEventAcksSerializeable } from 'sb-lib/types';
import { ConnectOptions, WebRtcClient, WebRtcClientEvent } from 'sb-lib/webrtc/client';
import { TwilioVideoWebRtcClient } from 'sb-lib/webrtc/twilio-video';
import { makeLog } from 'shared/utils/log';
import {
  ChatExpandedEvent,
  ChatExpandedEventType,
  DrawEvent,
  DrawEventType,
  TakeoverEvent,
  WidgetExtentEvent,
  WidgetExtentEventType,
} from 'types';

global.process = processBrowserShim;

const log = makeLog('sb-peer');

const CONNECT_TIMEOUT = 15000;

export type SbPeerStatus = 'disconnected' | 'connecting' | 'connected';

enum SbPeerEvent {
  connect = 'connect',
  data = 'data',
  disconnect = 'disconnect',
  error = 'error',
  streamout = 'streamout',
  streamin = 'streamin',
  status = 'status',
}

type FrontendPeerData = Chunk | SbPeerInternalMessage;

type SbPeerEventHandler = {
  [SbPeerEvent.connect]: (data: SbPeerConnect) => void;
  [SbPeerEvent.data]: (data: Chunk) => void;
  [SbPeerEvent.disconnect]: (code: SbPeerDisconnectCode) => void;
  [SbPeerEvent.error]: (msg: string) => void;
  [SbPeerEvent.streamout]: (stream: MediaStream) => void;
  [SbPeerEvent.streamin]: (data: SbPeerStreamChange) => void;
  [SbPeerEvent.status]: (status: SbPeerStatus) => void;
};

interface StreamHintMessage {
  type: 'streamHint';
  hint: {
    streamId: string;
    streamType: SbMediaStreamType;
  };
}

type SbPeerSendData =
  | {
      type: 'event';
      event: TakeoverEvent;
    }
  | {
      type: DrawEventType;
      event: DrawEvent;
    }
  | {
      type: 'endCall';
    }
  | {
      type: 'feDashboardState';
      feDashboardState: {
        micStatus: 'on' | 'off';
        camStatus: 'on' | 'off';
        isRecording: boolean;
      };
    }
  | {
      type: 'rrwebEventAcks';
      visitorSid: string;
      acks: RrwebEventAcksSerializeable;
    }
  | {
      type: WidgetExtentEventType;
      event: WidgetExtentEvent;
    }
  | {
      type: ChatExpandedEventType;
      event: ChatExpandedEvent;
    }
  | StreamHintMessage;

type SbPeerInternalMessage = StreamHintMessage;

type SbPeerStreamChange = {
  type: SbMediaStreamType;
  stream: null | MediaStream;
  isReceivingVideo: boolean;
  isReceivingAudio: boolean;
};

type SbPeerConnect = {
  connectTime: number;
};

interface SbPeerEmitter extends Events.EventEmitter {
  on: <T extends SbPeerEvent>(k: T, cb: SbPeerEventHandler[T]) => this;
  emit(event: SbPeerEvent, ...args: any[]): boolean;
}

export enum SbPeerDisconnectCode {
  CLOSED = 'CLOSED',
  ERROR = 'ERROR',
}

/**
 *
 * ServiceBell SimplePeer wrapper
 *
 */

export class SbPeer {
  #sbEvents = new Events.EventEmitter() as SbPeerEmitter;
  #interval = 0;
  #status = 'disconnected' as SbPeerStatus;
  #connectTimeout = 0;
  #connectStart = 0;
  #mediaStreams: SbMediaStreams;
  #isRecording = false;
  #client: null | WebRtcClient = null;

  constructor(mediaStreams: SbMediaStreams) {
    this.#mediaStreams = mediaStreams;
    mediaStreams.onStreamChange(({ streamType, stream }) =>
      this.#onStreamOutChange(streamType, stream),
    );
  }

  /**
   * Exposed methods
   */
  connect(token?: string, room?: string) {
    const options: ConnectOptions = {
      token: token,
      room: room,
    };

    if (!this.#client) {
      this.#client = new TwilioVideoWebRtcClient(this.#mediaStreams, log);
    }

    this.#client.connect(options);

    if (this.#client.isConnected()) {
      this.#setStatus('connected');
    } else {
      this.#connectStart = Date.now();
      this.#setStatus('connecting');
    }

    this.#client.removeAllListeners();

    this.#client.on(WebRtcClientEvent.connect, () => {
      this.#cancelConnectTimeout();
      const elapsed = Date.now() - this.#connectStart;
      log.debug(`Connected to peer in ${elapsed}ms`);
      this.#sbEvents.emit(SbPeerEvent.connect, { elapsed });
      this.#setStatus('connected');
      this.#reconnectMediaStreams();
    });

    this.#client.on(WebRtcClientEvent.data, buffer => {
      const data = JSON.parse(buffer) as FrontendPeerData;
      // Bubble up chunkosaurus data, those are 'external'
      if (isChunk(data)) {
        this.#sbEvents.emit(SbPeerEvent.data, data);
        return;
      }
    });

    this.#client.on(WebRtcClientEvent.streamin, (stream, type) => {
      log.debug(`New incoming stream ${stream?.id}`);
      const data = {
        type,
        stream: stream || null,
        isReceivingAudio: !!stream?.getAudioTracks().length,
        isReceivingVideo: !!stream?.getVideoTracks().length,
      };
      log.log(
        `Incoming ${type} stream change, video: ${data.isReceivingVideo}, audio: ${
          data.isReceivingAudio
        }, id: ${data.stream?.id || 'n/a'}`,
      );
      this.#sbEvents.emit(SbPeerEvent.streamin, data);
    });

    this.#client.on(WebRtcClientEvent.disconnect, (code: SbPeerDisconnectCode) => {
      this.#setDisconnected(code);
    });

    this.#resetConnectTimeout();
  }

  disconnect() {
    this.#client?.disconnect();
    this.#setDisconnected(SbPeerDisconnectCode.CLOSED);
  }

  sendTakeoverEvent(event: TakeoverEvent) {
    this.#sendData({ type: 'event', event });
  }

  sendWidgetExtent(event: WidgetExtentEvent) {
    this.#sendData({ type: 'widgetExtent', event });
  }

  sendChatExpanded(event: ChatExpandedEvent) {
    this.#sendData({ type: 'chatExpanded', event });
  }

  sendDrawEvent(event: DrawEvent) {
    this.#sendData({ type: 'draw', event });
  }

  sendEndCall() {
    this.#sendData({ type: 'endCall' });
  }

  sendFeDashboardState(args?: { isRecording?: boolean }) {
    if (args?.isRecording !== undefined) {
      this.#isRecording = args.isRecording;
    }
    this.#sendFeDashboardState();
  }

  sendRrwebEventAcks(visitorSid: string, acks: RrwebEventAcksSerializeable) {
    this.#sendData({ type: 'rrwebEventAcks', visitorSid, acks });
  }

  getIncomingCameraStream() {
    return this.#client?.getIncomingCameraStream();
  }

  getIncomingDisplayStream() {
    return this.#client?.getIncomingDisplayStream();
  }

  setOutgoingAudioEnabled(isEnabled: boolean) {
    this.#client?.setOutgoingAudioEnabled(isEnabled);
  }

  setOutgoingVideoEnabled(isEnabled: boolean) {
    this.#client?.setOutgoingVideoEnabled(isEnabled);
  }

  /*
   * Exposed events
   */

  onConnect(cb: (data: SbPeerConnect) => void) {
    this.#sbEvents.on(SbPeerEvent.connect, cb);
  }

  onStatus(cb: (status: SbPeerStatus) => void) {
    this.#sbEvents.on(SbPeerEvent.status, cb);
  }

  onData(cb: (data: Chunk) => void) {
    this.#sbEvents.on(SbPeerEvent.data, cb);
  }

  onDisconnect(cb: (code: SbPeerDisconnectCode) => void) {
    this.#sbEvents.on(SbPeerEvent.disconnect, cb);
  }

  onError(cb: (message: string) => void) {
    this.#sbEvents.on(SbPeerEvent.error, cb);
  }

  onStreamInChange(cb: (data: SbPeerStreamChange) => void) {
    this.#sbEvents.on(SbPeerEvent.streamin, cb);
  }

  isDataChannelOpen(): boolean {
    return this.#client?.isDataChannelOpen() || false;
  }

  /**
   * Private methods
   */

  #setDisconnected(code: SbPeerDisconnectCode) {
    clearInterval(this.#interval);
    this.#cancelConnectTimeout();
    this.#sbEvents.emit(SbPeerEvent.disconnect, code);
    this.#setStatus('disconnected');
  }

  #setStatus(status: SbPeerStatus) {
    if (status !== this.#status) {
      this.#sbEvents.emit(SbPeerEvent.status, status);
    }
    this.#status = status;
  }

  #emitError(errMsg: string) {
    this.#sbEvents.emit(SbPeerEvent.error, errMsg);
  }

  #resetConnectTimeout() {
    clearTimeout(this.#connectTimeout);
    this.#connectTimeout = window.setTimeout(() => {
      if (this.#client?.isConnected()) {
        log.warn('Tried to disconnect a live connection. This should not happen.');
        return;
      }

      log.debug(`Waited ${CONNECT_TIMEOUT}ms to negotiate connection, connect failed`);
      this.disconnect();
      this.#emitError(
        'Peer connection timed out. Visitor may be on a slow or inaccessible network, or have WebRTC disabled.',
      );
    }, CONNECT_TIMEOUT);
  }

  #cancelConnectTimeout() {
    clearTimeout(this.#connectTimeout);
  }

  #sendData(data: SbPeerSendData) {
    if (this.#client && this.#client.isDataChannelOpen()) {
      this.#client.sendData(JSON.stringify(data));
    }
  }

  // pull this into redux/manager and give it an action if it gets too spicy
  #sendFeDashboardState() {
    const audioTrack = this.#mediaStreams.getCameraAudioTrack();
    const videoTrack = this.#mediaStreams.getCameraVideoTrack();
    this.#sendData({
      type: 'feDashboardState',
      feDashboardState: {
        micStatus: !!audioTrack && audioTrack.enabled ? 'on' : 'off',
        camStatus: !!videoTrack && videoTrack.enabled ? 'on' : 'off',
        isRecording: this.#isRecording,
      },
    });
  }

  #addOutgoingStream(streamType: SbMediaStreamType, stream: MediaStream | null) {
    if (stream) {
      this.#client?.addStream(stream, streamType);
    }
  }

  #onStreamOutChange(streamType: SbMediaStreamType, stream: MediaStream | null) {
    if (!this.#client) return;

    // Clean up old stream
    const oldStream =
      streamType === SbMediaStreamType.camera
        ? this.#client.getOutgoingCameraStream()
        : this.#client.getOutgoingDisplayStream();
    if (oldStream) {
      try {
        this.#client.removeStream(oldStream);
      } catch (error) {
        // no-op, if the stream isn't there to remove then that's a-ok
      }
    }

    // Add new stream
    this.#addOutgoingStream(streamType, stream);
  }

  #reconnectMediaStreams() {
    const cameraStream = this.#mediaStreams.getCameraStream();
    if (cameraStream) {
      log.info('Reconnecting camera stream to reconnected peer');
      this.#addOutgoingStream(SbMediaStreamType.camera, cameraStream);
    }
    const displayStream = this.#mediaStreams.getDisplayStream();
    if (displayStream) {
      log.info('Reconnecting display stream to reconnected peer');
      this.#addOutgoingStream(SbMediaStreamType.display, displayStream);
    }
  }
}
